import {
  AppShell as MantineAppShell,
  Burger,
  Group,
  NavLink,
  Text,
  Avatar,
  Menu,
  UnstyledButton,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMsal } from '@azure/msal-react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useLocalAuth } from '../auth/local-auth';
import { Brand } from '../components/Brand';
import {
  IconLayoutDashboard,
  IconTruck,
  IconTruckDelivery,
  IconFile,
  IconBuilding,
  IconCar,
  IconUsers,
  IconUser,
  IconChartBar,
  IconSettings,
  IconHistory,
  IconLogout,
  IconMapPin,
  IconFileText,
} from '@tabler/icons-react';
import { CONDICIONES_URL } from '../legal/condiciones';

const navItems = [
  { to: '/', label: 'Dashboard', icon: IconLayoutDashboard },
  { to: '/envios', label: 'Envíos', icon: IconTruck },
  { to: '/deca', label: 'DeCA', icon: IconFile },
  { to: '/cargadores', label: 'Cargadores', icon: IconBuilding },
  { to: '/transportistas', label: 'Transportistas', icon: IconTruckDelivery },
  { to: '/origenes-destinos', label: 'Orígenes/Destinos', icon: IconMapPin },
  { to: '/vehiculos', label: 'Vehículos', icon: IconCar },
  { to: '/conductores', label: 'Conductores', icon: IconUsers },
  { to: '/usuarios', label: 'Usuarios', icon: IconUser },
  { to: '/informes', label: 'Informes', icon: IconChartBar },
  { to: '/auditoria', label: 'Auditoría', icon: IconHistory },
  { to: '/configuracion', label: 'Configuración', icon: IconSettings },
];

export function AppShell() {
  const [opened, { toggle }] = useDisclosure();
  const { instance, accounts } = useMsal();
  const local = useLocalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const account = accounts[0];

  // Prioridad: sesion local (email+password) -> MSAL -> fallback.
  const displayName = local.user?.name ?? account?.name ?? 'Usuario';
  const displayEmail = local.user?.email ?? account?.username ?? '';
  const displayInitial = (displayName?.[0] ?? 'U').toUpperCase();

  const handleLogout = () => {
    // Limpiar sesion local primero (es sincrono).
    if (local.isAuthenticated) {
      local.logout();
      // navigate a /login ya lo hace el AuthGuard al re-renderizar.
      navigate('/login', { replace: true });
      return;
    }
    // Si estamos en MSAL, redirige al logout de Microsoft.
    instance.logoutRedirect();
  };

  return (
    <MantineAppShell
      header={{ height: 60 }}
      navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <MantineAppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Brand size="md" />
          </Group>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <UnstyledButton>
                <Group gap="sm">
                  <Avatar size="sm" radius="xl" color="deca">
                    {displayInitial}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lh={1}>
                      {displayName}
                    </Text>
                    <Text size="xs" c="dimmed" lh={1} truncate>
                      {displayEmail}
                    </Text>
                  </div>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{displayEmail || 'Sesion activa'}</Menu.Label>
              <Menu.Divider />
              <Menu.Item
                component="a"
                href={CONDICIONES_URL}
                target="_blank"
                rel="noopener noreferrer"
                leftSection={<IconFileText size={14} />}
              >
                Condiciones del servicio
              </Menu.Item>
              <Menu.Item leftSection={<IconLogout size={14} />} onClick={handleLogout}>
                Cerrar sesion
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </MantineAppShell.Header>

      <MantineAppShell.Navbar p="sm">
        <ScrollArea>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active =
              item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                active={active}
                label={item.label}
                leftSection={<Icon size={18} />}
                onClick={() => navigate(item.to)}
                style={{ borderRadius: 4 }}
              />
            );
          })}
        </ScrollArea>
      </MantineAppShell.Navbar>

      <MantineAppShell.Main>
        <Outlet />
      </MantineAppShell.Main>
    </MantineAppShell>
  );
}
